/* eslint-disable react/no-multi-comp */
import React, { FC, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import classnames from 'classnames';
import {
  BlueRefundIcon,
  CheckboxTickIcon,
  HyphenIcon,
  ListActionsIcon,
  PrintIcon,
  ShareIcon,
  VerticalListActionsIcon
} from 'assets/icons';
import { CustomPopover, GenericTable } from 'components';
import CustomAvatar from 'components/avatar/CustomAvatar';
import CustomCheckbox from 'components/custom-checkbox/CustomCheckbox';
import {
  selectCurrentTeamCustomersPermissions,
  selectCurrentTeamRefundPermissions,
  selectUserDetails
} from 'containers/app/appSlice';
import { useAppDispatch, useAppSelector } from 'hooks';
import { twMerge } from 'tailwind-merge';
import { clevertapEvents } from 'types/baseTypes';
import { formatAmount, getAmountWithCurrency } from 'utils/amountUtils';
import { logAnalyticEvent } from 'utils/analytics';
import DateUtils from 'utils/dateUtils';
import { getInitials } from 'utils/commonUtils';
import { getStringFiller } from 'utils/stringUtils';
import { TableChildRow, TableChildRowVariant, TableColumn } from 'components/generic-table/types';
import { useLazyDownloadPrintReceiptAsPDFQuery } from '../api';
import { DisputeDetails, Receipt, RefundTxnInList, ReturnTxnInList } from '../receipt.model';
import { ReceiptColumnInfo } from '../receipts.model';
import { updateRefundAmount, updateRefundReason } from '../receiptsSlice';
import {
  getPayoutStatus,
  getPayoutStatusColor,
  getReceiptPaymentMethodIconSrc,
  getReceiptPaymentMethodLabel,
  getReceiptStatus,
  getReceiptStatusColor,
  shouldEnableRefund
} from '../util';
import { getDisputeStatusColor } from 'containers/disputes/utils';
import { PaymentSystem } from 'containers/payouts/constants';
import OtherPaymentIcon from 'assets/icons/OtherPaymentIcon.svg';

interface ReceiptTableRowContext {
  isChildRow?: boolean;
  rowVariant?: 'refund' | 'dispute' | 'return';
  parentItem?: Receipt;
}

type ReceiptRowData = Receipt & ReceiptTableRowContext;

interface ReceiptsTableProps {
  receiptsList: Receipt[];
  onReceiptClick: (receiptId: string) => void;
  onPayoutClick: (payoutId: string, receiptId: string, paymentSystem: PaymentSystem) => void;
  onRefundTransactionClick: (receiptId: string) => void;
  onDisputedTransactionClick: (disputeId: string, receiptId: string) => void;
  onShareReceiptClick: (invoiceId: string, receiptId: string) => void;
  onCreateRefundClick: (receiptId: string) => void;
  currentSelectedId: string;
  columnInfo?: ReceiptColumnInfo[];
  onSortChange?: (sortBy: string, sortOrder: string) => void;
  currentSortBy?: string;
  currentSortOrder?: string;
  stickyHorizontalScrollbar?: { bottomOffsetPx: number };
  selectedReceipts?: Receipt[];
  onSelectionChange?: (selectedItems: Receipt[]) => void;
}

const ReceiptRowActions: FC<{
  receipt: Receipt;
  onShareReceiptClick: (invoiceId: string, receiptId: string) => void;
  onCreateRefundClick: (receiptId: string) => void;
}> = ({ receipt, onShareReceiptClick, onCreateRefundClick }) => {
  const [showActionsPopover, setShowActionsPopover] = useState(false);
  const [downloadPrintReceiptAsPDF] = useLazyDownloadPrintReceiptAsPDFQuery();
  const dispatch = useAppDispatch();
  const currentUser = useAppSelector(selectUserDetails);
  const currentRefundPermissions = useAppSelector(selectCurrentTeamRefundPermissions);
  const customerPermissions = useAppSelector(selectCurrentTeamCustomersPermissions);

  const isShareEnabled =
    customerPermissions.viewCustomersCreatedByTeam ||
    customerPermissions.editCustomersCreatedByTeam ||
    customerPermissions.editExistingCustomers;

  const enableRefund = shouldEnableRefund(
    receipt.isManualPayment,
    currentRefundPermissions,
    receipt.merchantId === currentUser.id,
    receipt.refundTransactions && receipt.refundTransactions[0]?.status !== '',
    receipt.paymentMethod,
    receipt.isRefundExpired
  );

  const ListActionsAnchor: React.FC<{ isPanelOpen: boolean }> = ({ isPanelOpen }) => (
    <div id="receipts-table-more-options" className="py-2">
      <ListActionsIcon
        className={twMerge('hidden shrink-0 lg:flex', classnames({ 'fill-current text-secondary': isPanelOpen }))}
      />
      <VerticalListActionsIcon
        className={twMerge('mx-2 flex shrink-0 lg:hidden', classnames({ 'fill-current text-secondary': isPanelOpen }))}
      />
    </div>
  );

  return (
    <CustomPopover
      showArrow={true}
      arrowOffset="right-6"
      highlightAnchor={false}
      anchorComponent={ListActionsAnchor}
      zIndex="z-[101]"
      offset="-right-6"
      margin="mt-1"
      onClosePopover={() => setShowActionsPopover(false)}
      show={showActionsPopover}
      onTogglePopover={() => setShowActionsPopover(prev => !prev)}>
      <div className="space-y-3 text-sbase font-semibold text-primaryText">
        {isShareEnabled && (
          <div
            id="receipts-table-3dot-share-receipt"
            className="flex flex-row items-center gap-2"
            onClick={e => {
              setShowActionsPopover(false);
              e.stopPropagation();
              onShareReceiptClick(receipt.invoiceId, receipt.id);
              logAnalyticEvent(clevertapEvents.webReceiptsListMMShare, { 'Receipt Id': receipt.id });
            }}>
            <ShareIcon />
            <div>Share Receipt</div>
          </div>
        )}
        <div
          id="receipts-table-3dot-print-receipt"
          onClick={e => {
            e.stopPropagation();
            setShowActionsPopover(false);
            downloadPrintReceiptAsPDF({ receiptId: receipt.id });
            logAnalyticEvent(clevertapEvents.webReceiptsListMMPrint, { 'Receipt Id': receipt.id });
          }}
          className="flex flex-row items-center gap-2">
          <PrintIcon />
          <div>Print</div>
        </div>
        {enableRefund && (
          <div
            id="receipts-table-3dot-refund-receipt"
            onClick={e => {
              e.stopPropagation();
              setShowActionsPopover(false);
              dispatch(updateRefundReason(''));
              dispatch(updateRefundAmount(''));
              onCreateRefundClick(receipt.id);
              logAnalyticEvent(clevertapEvents.webReceiptsListMMRefund, { 'Receipt Id': receipt.id });
            }}
            className="flex flex-row items-center gap-2">
            <BlueRefundIcon />
            <div>Refund</div>
          </div>
        )}
      </div>
    </CustomPopover>
  );
};

const getReceiptAmount = (receipt: Receipt) => {
  if (+receipt.tipAmount > 0) {
    return getAmountWithCurrency(+receipt.payableAmount - +receipt.tipAmount);
  }
  return getAmountWithCurrency(receipt.payableAmount);
};

const getCustomerDisplay = (receipt: Receipt) => {
  return receipt.customerName || receipt.customerPhone || receipt.customerEmail || '-';
};

const ReceiptsTable: FC<ReceiptsTableProps> = ({
  receiptsList,
  onReceiptClick,
  onPayoutClick,
  onRefundTransactionClick,
  onDisputedTransactionClick,
  onShareReceiptClick,
  onCreateRefundClick,
  currentSelectedId,
  columnInfo,
  onSortChange,
  currentSortBy = 'createdAt',
  currentSortOrder = 'desc',
  stickyHorizontalScrollbar,
  selectedReceipts = [],
  onSelectionChange
}) => {
  const { rootReceiptId } = useParams();

  const getDateCell = (row: ReceiptRowData) => {
    if (row.isChildRow) {
      const dateValue = row.createdAt as unknown as string;
      if (!dateValue) return <div className="text-sbase text-primaryText">-</div>;
      return (
        <div className="text-sbase text-primaryText">
          {DateUtils.getDateInFormat({ date: new Date(dateValue), dateFormat: 'dd MMM yyyy' })}
        </div>
      );
    }
    if (!row.createdAt) return <div className="text-sbase text-primaryText">-</div>;
    const dateObj = new Date(row.createdAt);
    return (
      <div className="text-sbase text-primaryText">
        {DateUtils.getDateInFormat({ date: dateObj, dateFormat: 'dd MMM yyyy' })}
      </div>
    );
  };

  const getChildReceiptNumber = (row: ReceiptRowData): string => {
    if (!row.isChildRow) {
      return row.receiptNumber || row.id;
    }
    if (row.rowVariant === 'dispute') {
      return (row as unknown as DisputeDetails).disputeId;
    }
    return row.id;
  };

  const getDisputeRows = (receipt: Receipt): DisputeDetails[] => {
    const disputes = receipt.disputeTransactions as DisputeDetails[] | DisputeDetails | null;
    if (Array.isArray(disputes)) {
      return disputes;
    }
    if (disputes) {
      return [disputes];
    }
    return [];
  };

  const getChildAmount = (row: ReceiptRowData): string => {
    if (!row.isChildRow) {
      return getReceiptAmount(row);
    }
    if (row.rowVariant === 'dispute') {
      const dispute = row as unknown as DisputeDetails;
      return getAmountWithCurrency(dispute.disputeAmount);
    }
    return getAmountWithCurrency(row.amount);
  };

  const getAmountCell = (row: ReceiptRowData) => {
    const showTip = !row.isChildRow && Boolean(row.tipAmount) && +row.tipAmount > 0;

    return (
      <div className="flex flex-col">
        <div className="truncate text-sbase font-semibold text-primaryText">{getChildAmount(row)}</div>
        {showTip && <div className="truncate text-px13 text-accentText">{`+$${formatAmount(row.tipAmount)}`}</div>}
      </div>
    );
  };

  const getChildStatus = (row: ReceiptRowData) => {
    const status = row.status;
    const color = row.rowVariant === 'dispute' ? getDisputeStatusColor(status) : getReceiptStatusColor(status);
    const maxWidthClass = row.rowVariant === 'dispute' ? 'max-w-[135px]' : 'max-w-[120px]';
    return (
      <div className={`min-w-[80px] ${maxWidthClass} text-sbase font-bold`} style={{ color }}>
        {getReceiptStatus(status)}
      </div>
    );
  };

  const getReceiptChildCellClassName = (
    variant?: TableChildRowVariant,
    isFirstCell?: boolean,
    isLastCell?: boolean
  ) => {
    const bgClass = variant === 'dispute' ? 'bg-[#FBF1DF]' : 'bg-secondaryBtn';
    if (isFirstCell) {
      return `${bgClass} rounded-l-md border-0`;
    }
    if (isLastCell) {
      return `${bgClass} rounded-r-md border-0`;
    }
    return bgClass;
  };

  const getPaymentMethodCell = (row: ReceiptRowData) => {
    if (row.isChildRow) {
      return <div />;
    }

    const label = getReceiptPaymentMethodLabel({
      paymentMethod: row.paymentMethod,
      cardDetails: row.cardDetails,
      paymentMethodIcon: row.paymentMethodIcon
    });
    if (!label || label === '-') {
      return <div className="text-sbase text-primaryText">-</div>;
    }
    const iconSrc = getReceiptPaymentMethodIconSrc({
      paymentMethod: row.paymentMethod,
      cardDetails: row.cardDetails,
      paymentMethodIcon: row.paymentMethodIcon
    });
    return (
      <div className="flex items-center gap-1.5">
        <img
          src={iconSrc}
          alt=""
          className="h-3 w-5 shrink-0 object-contain"
          onError={e => {
            e.currentTarget.onerror = null;
            e.currentTarget.src = OtherPaymentIcon;
          }}
        />
        <span className="truncate text-sbase text-primaryText">{label}</span>
      </div>
    );
  };

  const getCustomerCell = (row: ReceiptRowData) => {
    const parent = row.isChildRow ? row.parentItem : row;
    const customer = parent ? getCustomerDisplay(parent) : '-';
    return (
      <div className="truncate text-sbase text-primaryText" title={customer}>
        {getStringFiller(customer === '_' ? '' : customer)}
      </div>
    );
  };

  const getTipCell = (row: ReceiptRowData) => {
    if (row.isChildRow) {
      return <div />;
    }
    if (!row.tipAmount || +row.tipAmount <= 0) {
      return <div className="text-sbase text-primaryText">-</div>;
    }
    return <div className="truncate text-sbase text-primaryText">{getAmountWithCurrency(+row.tipAmount)}</div>;
  };

  const getPayoutCell = (row: ReceiptRowData) => {
    if (row.isChildRow) {
      return <div />;
    }
    if (!row.payoutId) {
      return <div className="text-sbase text-primaryText">NA</div>;
    }
    return (
      <div className="flex flex-col gap-1">
        <div
          className="truncate text-sbase font-semibold text-secondary underline decoration-secondary underline-offset-2"
          onClick={e => {
            e.stopPropagation();
            onPayoutClick(row.payoutId, row.id, row.paymentSystem as PaymentSystem);
          }}>
          {row.payoutId}
        </div>
        <div className="truncate text-px13 font-semibold" style={{ color: getPayoutStatusColor(row.payoutStatus) }}>
          {getPayoutStatus(row.payoutStatus)}
        </div>
      </div>
    );
  };

  const getCreatedByCell = (row: ReceiptRowData) => {
    if (row.isChildRow) {
      return <div />;
    }
    return (
      <div className="flex items-center space-x-2">
        <CustomAvatar
          src={row.merchantProfilePicture}
          label={getInitials(row.merchantName || '')}
          height="h-6"
          width="w-6"
          className="rounded-full"
          fontStyle="text-[8px]"
        />
        <span className="truncate text-sbase text-primaryText">{row.merchantName || '-'}</span>
      </div>
    );
  };

  const columns = useMemo<TableColumn<ReceiptRowData>[]>(() => {
    return [
      {
        id: 'checkbox',
        header: ({ selectedItems, allItems, onSelectAll: onSelectAllClick }) => {
          const isAllSelected = (allItems?.length ?? 0) > 0 && (selectedItems?.length ?? 0) === allItems?.length;
          const isSomeSelected = (selectedItems?.length ?? 0) > 0;
          let headerIcon: React.ReactNode = null;
          if (isAllSelected) headerIcon = <CheckboxTickIcon className="h-3 w-3 text-white" />;
          else if (isSomeSelected) headerIcon = <HyphenIcon className="h-3 w-3 text-white" />;
          return (
            <div className="flex cursor-pointer items-center justify-center">
              <CustomCheckbox
                id="select-all-receipts"
                className={isSomeSelected || isAllSelected ? '' : 'bg-white'}
                isChecked={isSomeSelected || isAllSelected}
                onClick={() => onSelectAllClick?.()}>
                {headerIcon}
              </CustomCheckbox>
            </div>
          );
        },
        accessor: row => {
          if (row.isChildRow) return <div className="w-[56px]" />;
          const isChecked = row.selectedItems?.some((r: Receipt) => r.id === row.id) ?? false;
          return (
            <div
              className="flex cursor-pointer items-center justify-center"
              onClick={e => {
                e.stopPropagation();
                row.onItemSelect?.(row);
              }}>
              <CustomCheckbox
                className="shrink-0"
                id={`select-receipt-${row.id}`}
                isChecked={isChecked}
                onClick={() => row.onItemSelect?.(row)}>
                {isChecked && <CheckboxTickIcon className="h-3 w-3 text-white" />}
              </CustomCheckbox>
            </div>
          );
        },
        width: 'w-[56px]'
      },
      {
        id: 'createdAt',
        header: 'Date',
        accessor: row => getDateCell(row),
        width: 'min-w-[200px] w-[200px]',
        sortable: true
      },
      {
        id: 'payableAmount',
        header: 'Amount',
        accessor: row => getAmountCell(row),
        width: 'w-[150px]',
        sortable: true
      },
      {
        id: 'tipAmount',
        header: 'Tip collected',
        accessor: row => getTipCell(row),
        width: 'w-[150px]',
        sortable: true
      },
      {
        id: 'paymentMethod',
        header: 'Payment method',
        accessor: row => getPaymentMethodCell(row),
        width: 'w-[200px]'
      },
      {
        id: 'status',
        header: 'Status',
        accessor: row => getChildStatus(row),
        width: 'w-[150px]',
        sortable: true
      },
      {
        id: 'customerName',
        header: 'Customer',
        accessor: row => getCustomerCell(row),
        width: 'w-[180px]',
        sortable: true
      },
      {
        id: 'receiptNumber',
        header: 'Receipt No.',
        accessor: row => (
          <div
            className={`truncate text-sbase ${row.isChildRow ? 'font-normal' : 'font-semibold'} text-primaryText`}
            title={getChildReceiptNumber(row)}>
            {getChildReceiptNumber(row)}
          </div>
        ),
        width: 'min-w-[160px] w-[160px]',
        sortable: true
      },
      {
        id: 'payoutId',
        header: 'Payout',
        accessor: row => getPayoutCell(row),
        width: 'w-[150px]'
      },
      {
        id: 'merchantName',
        header: 'Created by',
        accessor: row => getCreatedByCell(row),
        width: 'w-[200px]',
        sortable: true
      },
      {
        id: 'actions',
        header: '',
        accessor: row => {
          if (row.isChildRow) return null;
          return (
            <ReceiptRowActions
              receipt={row}
              onShareReceiptClick={onShareReceiptClick}
              onCreateRefundClick={onCreateRefundClick}
            />
          );
        },
        width: 'w-[80px]'
      }
    ];
  }, [onShareReceiptClick, onCreateRefundClick, onPayoutClick]);

  const getChildRows = (receipt: Receipt): TableChildRow<Receipt>[] => {
    const childRows: TableChildRow<Receipt>[] = [];

    receipt.refundTransactions?.forEach(refund => {
      childRows.push({
        item: refund as unknown as Receipt,
        rowId: `refund-${refund.id}`,
        variant: 'refund',
        parentItem: receipt
      });
    });

    getDisputeRows(receipt).forEach(dispute => {
      childRows.push({
        item: dispute as unknown as Receipt,
        rowId: `dispute-${dispute.disputeId}`,
        variant: 'dispute',
        parentItem: receipt
      });
    });

    receipt.returnTransactions?.forEach(returnTxn => {
      childRows.push({
        item: returnTxn as unknown as Receipt,
        rowId: `return-${returnTxn.id}`,
        variant: 'return',
        parentItem: receipt
      });
    });

    return childRows;
  };

  const textColumns = ['receiptNumber', 'customerName', 'merchantName', 'status'];

  return (
    <div className="w-full">
      <GenericTable
        data={receiptsList}
        columns={columns}
        onRowClick={receipt => {
          onReceiptClick(receipt.id);
          logAnalyticEvent(clevertapEvents.webReceiptsListDetails, { 'Receipt Id': receipt.id });
        }}
        className="w-full"
        enableRowHover={true}
        textColumns={textColumns}
        columnInfo={columnInfo}
        highlightedRowId={currentSelectedId || rootReceiptId}
        showCheckbox={true}
        selection={{
          getItemId: receipt => receipt.id,
          selectedItems: selectedReceipts,
          onItemSelect: item => {
            if (!onSelectionChange) return;
            const receipt = receiptsList.find(r => r.id === item.id);
            if (!receipt) return;
            const alreadySelected = selectedReceipts.some(r => r.id === receipt.id);
            onSelectionChange(
              alreadySelected ? selectedReceipts.filter(r => r.id !== receipt.id) : [...selectedReceipts, receipt]
            );
          },
          onSelectAll: () => {
            if (!onSelectionChange) return;
            const allSelected = receiptsList.every(r => selectedReceipts.some(s => s.id === r.id));
            onSelectionChange(allSelected ? [] : receiptsList);
          },
          allItems: receiptsList
        }}
        childRows={{
          getChildRows,
          getChildCellClassName: ({ variant, isFirstCell, isLastCell }) =>
            getReceiptChildCellClassName(variant, isFirstCell, isLastCell),
          showSpacerBeforeChild: true,
          showGroupFooterAfterLastChild: true,
          onChildRowClick: (childRow, e) => {
            e.stopPropagation();
            if (childRow.variant === 'refund') {
              onRefundTransactionClick((childRow.item as unknown as RefundTxnInList).id);
            } else if (childRow.variant === 'dispute') {
              onDisputedTransactionClick(
                (childRow.item as unknown as DisputeDetails).disputeId,
                childRow.parentItem.id
              );
            } else if (childRow.variant === 'return') {
              onReceiptClick((childRow.item as unknown as ReturnTxnInList).id);
            }
          }
        }}
        sorting={{
          sortBy: currentSortBy,
          sortOrder: currentSortOrder,
          enableMultiSort: false,
          onSortChange: (sortBy, sortOrder) => {
            onSortChange?.(sortBy, sortOrder);
          }
        }}
        stickyHorizontalScrollbar={stickyHorizontalScrollbar}
      />
    </div>
  );
};

export default ReceiptsTable;
